import { LocationStrategy, PathLocationStrategy, isPlatformBrowser } from '@angular/common'
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import {
  type ApplicationConfig,
  PLATFORM_ID,
  SecurityContext,
  importProvidersFrom,
  inject,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BrowserModule } from '@angular/platform-browser'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideRouter, withComponentInputBinding } from '@angular/router'

import { AuthInterceptor } from '@interceptors/http-interceptor'
import { provideTransloco } from '@jsverse/transloco'
import Aura from '@primeuix/themes/aura'
import { ValidationService } from '@services/core/validation.service'
import { LayoutService } from '@services/layout/layout.service'
import { LoadingService } from '@services/shared/loading.service'
import { SweetAlertsService } from '@services/shared/sweet-alerts.service'
import { provideLottieOptions } from 'ngx-lottie'
import { provideMarkdown } from 'ngx-markdown'
import primelocaleEs from 'primelocale/es.json'
import { ConfirmationService, MessageService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { routes } from './app.routes'
import { TranslocoHttpLoader } from './transloco-loader'

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    importProvidersFrom(BrowserModule, FormsModule, ReactiveFormsModule),
    provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
    provideAnimations(),
    provideLottieOptions({
      player: () => {
        // Solo cargar lottie-web en el browser (no en SSR)
        if (isPlatformBrowser(inject(PLATFORM_ID))) {
          return import('lottie-web')
        }
        return Promise.resolve(null as any)
      },
    }),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          prefix: 'p',
          darkModeSelector: 'none',
          cssLayer: false,
        },
      },
      ripple: true,
      translation: primelocaleEs.es,
    }),

    { provide: LocationStrategy, useClass: PathLocationStrategy },
    MessageService,
    LayoutService,
    SweetAlertsService,
    ValidationService,
    ConfirmationService,
    LoadingService,
    DynamicDialogRef,
    DynamicDialogConfig,
    DialogService,
    provideTransloco({
      config: {
        availableLangs: ['en', 'es'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),

    // 🔥 Markdown rendering para mensajes del chat
    provideMarkdown({
      sanitize: SecurityContext.NONE, // Permitimos todo el HTML generado por marked
    }),
  ],
}
